import React, { useState } from "react";
import axios from "axios";
import { validate } from "rut.js";

import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Grid,
  Typography,
  Alert,
  IconButton,
  Stack,
  InputAdornment,
  Box,
  Fade,
  useTheme,
  useMediaQuery
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";

import LogoRFID from "../imagenes/logorfid.png";

export default function EditarUsuarioModal({ usuarios }) {
  const URI = "https://demo.hermescloud.site/web/actualizar_usuario.php";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [nombre, setNombre] = useState(usuarios.nombre);
  const [apellido, setApellido] = useState(usuarios.apellido);
  const [rut, setRut] = useState(usuarios.rut);
  const [clave, setClave] = useState(usuarios.clave);

  const [error, setError] = useState("");
  const [rutError, setRutError] = useState("");

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setError("");
    setRutError("");
  };

  const Guardar = async (e) => {
    e.preventDefault();

    if (!nombre || !apellido || !rut || !clave) {
      setError("Completa todos los campos");
      return;
    }

    if (!validate(rut)) {
      setRutError("RUT inválido");
      return;
    }

    try {
      setLoading(true);

      await axios.post(URI, {
        id: usuarios.idUsuarios,
        nombre,
        apellido,
        rut,
        clave
      });

      handleClose();
      window.location.reload();
    } catch (err) {
      setError("Error al actualizar usuario");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* BOTÓN EDITAR */}
      <IconButton
        onClick={handleOpen}
        sx={{
          background: "#f5f5f5",
          "&:hover": { background: "#e0e0e0" }
        }}
      >
        <EditIcon />
      </IconButton>

      {/* MODAL */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        TransitionComponent={Fade}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 4, overflow: "hidden" }
        }}
      >
        {/* HEADER MODERNO */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            py: { xs: 2, md: 3 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(90deg, #1976d2, #42a5f5)",
            color: "white"
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Editar Usuario
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Actualiza los datos del usuario
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* FORMULARIO */}
        <form onSubmit={Guardar}>
          <DialogContent dividers sx={{ p: { xs: 2, sm: 3, md: 4 }, overflowY: "visible" }}>
            <Grid container spacing={4}>
              {/* IZQUIERDA: FORM */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>
                  {error && <Alert severity="error">{error}</Alert>}

                  <TextField
                    label="RUT"
                    fullWidth
                    value={rut}
                    onChange={(e) => {
                      setRut(e.target.value);
                      setRutError("");
                    }}
                    error={!!rutError}
                    helperText={rutError}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label="Nombre"
                    fullWidth
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label="Apellido"
                    fullWidth
                    value={apellido}
                    onChange={(e) => setApellido(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      )
                    }}
                  />

                  <TextField
                    label="Contraseña"
                    fullWidth
                    type="password"
                    value={clave}
                    onChange={(e) => setClave(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Stack>
              </Grid>

              {/* DERECHA: PANEL INFO */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    background: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    textAlign: "center"
                  }}
                >
                  <img src={LogoRFID} alt="logo" style={{ width: 100, opacity: 0.9 }} />
                  <Typography mt={2} fontWeight={500}>
                    Información del Usuario
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Mantén los datos actualizados para un mejor control del sistema.
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* BOTONES */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end" mt={4}>
              <Button
                fullWidth={isMobile}
                onClick={handleClose}
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Cancelar
              </Button>

              <Button
                fullWidth={isMobile}
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  background: "linear-gradient(90deg, #1976d2, #42a5f5)"
                }}
              >
                {loading ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </DialogContent>
        </form>
      </Dialog>
    </>
  );
}